import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Table, Form, Button } from 'react-bootstrap';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import { useAppContext } from '../context/AppContext';

const getAuthToken = () => localStorage.getItem('authToken');

const MonthlyAttendancePage = () => {
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();
  const [searchParams] = useSearchParams();
  const batchIdFromURL = searchParams.get('batch_id');
  const dateFromURL = searchParams.get('date');

  const [selectedBatch, setSelectedBatch] = useState(batchIdFromURL || '');
  const [month, setMonth] = useState(
    dateFromURL
      ? dateFromURL.slice(0, 7)
      : new Date().toISOString().slice(0, 7),
  );
  const [attendanceData, setAttendanceData] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchBatches();
  }, [selectedAcademicYear]);

  useEffect(() => {
    if (selectedBatch) {
      fetchAttendance();
    }
  }, [selectedBatch, month]);

  const fetchBatches = () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      setSelectedBatch('');
      setAttendanceData([]);
      return;
    }
    axios
      .get(`${appConstants.API_ROOT_URL}get-batches-v3`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academic_year_id: selectedAcademicYear.id },
      })
      .then((response) => {
        const batchList = response.data.data || [];
        setBatches(batchList);
        if (batchList.length > 0) {
          setSelectedBatch((prev) => {
            const exists = batchList.some(
              (b) => String(b.id) === String(prev || batchIdFromURL),
            );
            if (exists) {
              return prev || batchIdFromURL;
            }
            return String(batchList[0].id);
          });
        } else {
          setSelectedBatch('');
          setAttendanceData([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching batches', error);
        setBatches([]);
      });
  };

  const fetchAttendance = () => {
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }
    axios
      .post(
        `${appConstants.API_ROOT_URL}get-monthly-attendance-v2`,
        { batch_id: selectedBatch, date: month + '-01' },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((response) => {
        setAttendanceData(response.data.attendance);
      })
      .catch((error) => {
        console.error('Error fetching attendance', error);
      });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return attendanceData;

    const sorted = [...attendanceData].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (sortConfig.key === 'present') {
        aValue = Object.values(a.attendance).filter((s) => s === 'P').length;
        bValue = Object.values(b.attendance).filter((s) => s === 'P').length;
      } else if (sortConfig.key === 'absent') {
        aValue = Object.values(a.attendance).filter((s) => s === 'A').length;
        bValue = Object.values(b.attendance).filter((s) => s === 'A').length;
      } else if (sortConfig.key === 'total') {
        const aStatuses = Object.values(a.attendance);
        const bStatuses = Object.values(b.attendance);
        aValue =
          aStatuses.filter((s) => s === 'P').length +
          aStatuses.filter((s) => s === 'A').length;
        bValue =
          bStatuses.filter((s) => s === 'P').length +
          bStatuses.filter((s) => s === 'A').length;
      } else if (sortConfig.key === 'percentage') {
        const aStatuses = Object.values(a.attendance);
        const bStatuses = Object.values(b.attendance);
        const aPre = aStatuses.filter((s) => s === 'P').length;
        const aTotal = aPre + aStatuses.filter((s) => s === 'A').length;
        const bPre = bStatuses.filter((s) => s === 'P').length;
        const bTotal = bPre + bStatuses.filter((s) => s === 'A').length;
        aValue = aTotal > 0 ? (aPre / aTotal) * 100 : 0;
        bValue = bTotal > 0 ? (bPre / bTotal) * 100 : 0;
      } else if (sortConfig.key.startsWith('date-')) {
        const date = sortConfig.key.replace('date-', '');
        aValue =
          a.attendance[date] === 'P' ? 2 : a.attendance[date] === 'A' ? 1 : 0;
        bValue =
          b.attendance[date] === 'P' ? 2 : b.attendance[date] === 'A' ? 1 : 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <i
          className="fas fa-sort ms-1 text-muted"
          style={{ fontSize: '0.8rem' }}
        ></i>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <i className="fas fa-sort-up ms-1" style={{ fontSize: '0.8rem' }}></i>
    ) : (
      <i className="fas fa-sort-down ms-1" style={{ fontSize: '0.8rem' }}></i>
    );
  };

  const handlePrint = () => {
    if (attendanceData.length === 0) {
      alert('No attendance data to print');
      return;
    }

    const selectedBatchName = batches.find(
      (b) => b.id === parseInt(selectedBatch),
    );
    const batchName = selectedBatchName
      ? `${selectedBatchName.class_name} ${selectedBatchName.division}`
      : 'Attendance';
    const monthName = new Date(month + '-01').toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });

    const printWindow = window.open('', '_blank');
    const dates = Object.keys(attendanceData[0].attendance);

    // Get sort info for display
    let sortInfo = '';
    if (sortConfig.key) {
      let columnName = '';
      if (sortConfig.key === 'name') columnName = 'Student Name';
      else if (sortConfig.key === 'present') columnName = 'Present';
      else if (sortConfig.key === 'absent') columnName = 'Absent';
      else if (sortConfig.key === 'total') columnName = 'Total';
      else if (sortConfig.key === 'percentage') columnName = 'Percentage';
      else if (sortConfig.key.startsWith('date-')) {
        const date = sortConfig.key.replace('date-', '');
        columnName = `Date ${new Date(date).getDate()}`;
      }
      const direction =
        sortConfig.direction === 'asc' ? 'Ascending' : 'Descending';
      sortInfo = `<p style="text-align: center; margin: 3px 0 8px 0; font-size: 10px; color: #666; font-style: italic;">Sorted by: ${columnName} (${direction})</p>`;
    }

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${batchName} - ${monthName} Attendance</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 10px;
            margin: 0;
            padding: 10px;
          }
          h3 {
            text-align: center;
            margin: 5px 0;
            font-size: 14px;
          }
          h4 {
            text-align: center;
            margin: 3px 0 10px 0;
            font-size: 12px;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
          }
          th, td {
            border: 1px solid #000;
            padding: 2px 3px;
            text-align: center;
          }
          th {
            background-color: #e0e0e0;
            font-weight: bold;
            font-size: 9px;
          }
          .student-name {
            text-align: left;
            font-size: 9px;
            max-width: 120px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .present { 
            background-color: #d4edda;
            font-weight: bold;
          }
          .absent { 
            background-color: #f8d7da;
            font-weight: bold;
          }
          /* Black and white print support */
          @media print {
            .present {
              background-color: #fff !important;
              border: 2px solid #000 !important;
            }
            .absent {
              background-color: #777 !important;
              color: #fff !important;
              border: 2px solid #000 !important;
            }
            .text-success {
              color: #000 !important;
              font-weight: bold !important;
            }
            .text-danger {
              color: #000 !important;
              font-weight: bold !important;
              text-decoration: underline !important;
            }
          }
          .summary-row {
            background-color: #e2e3e5;
            font-weight: bold;
          }
          .summary-col {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          .text-success { color: #28a745; }
          .text-danger { color: #dc3545; }
          .print-btn {
            margin: 10px 0;
            padding: 8px 16px;
            background: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
          }
          .print-btn:hover {
            background: #0056b3;
          }
        </style>
      </head>
      <body>
        <button class="print-btn no-print" onclick="window.print()">Print</button>
        <h3>${batchName}</h3>
        <h4>Monthly Attendance - ${monthName}</h4>
        ${sortInfo}
        <table>
          <thead>
            <tr>
              <th rowspan="2" style="min-width: 25px;">#</th>
              <th rowspan="2" style="min-width: 120px;">Student Name</th>
              <th colspan="${dates.length}">Date</th>
              <th rowspan="2">P</th>
              <th rowspan="2">A</th>
              <th rowspan="2">Total</th>
              <th rowspan="2">%</th>
            </tr>
            <tr>
              ${dates
                .map(
                  (date) =>
                    `<th style="min-width: 18px;">${new Date(
                      date,
                    ).getDate()}</th>`,
                )
                .join('')}
            </tr>
          </thead>
          <tbody>
    `;

    const sortedData = getSortedData();
    sortedData.forEach((student, index) => {
      const statuses = Object.values(student.attendance);
      const presentCount = statuses.filter((s) => s === 'P').length;
      const absentCount = statuses.filter((s) => s === 'A').length;
      const totalDays = presentCount + absentCount;
      const percentage =
        totalDays > 0 ? ((presentCount / totalDays) * 100).toFixed(1) : 0;

      html += `<tr>`;
      html += `<td>${index + 1}</td>`;
      html += `<td class="student-name">${student.name}</td>`;

      Object.values(student.attendance).forEach((status) => {
        const cellClass =
          status === 'P' ? 'present' : status === 'A' ? 'absent' : '';
        const symbol = status === 'P' ? '✓' : status === 'A' ? '✗' : '';
        html += `<td class="${cellClass}">${symbol}</td>`;
      });

      html += `<td class="summary-col text-success">${presentCount}</td>`;
      html += `<td class="summary-col text-danger">${absentCount}</td>`;
      html += `<td class="summary-col">${totalDays}</td>`;
      html += `<td class="summary-col">${percentage}%</td>`;
      html += `</tr>`;
    });

    // Daily Present Row
    html += `<tr class="summary-row">`;
    html += `<td colspan="2" style="text-align: left;">Daily Present</td>`;
    dates.forEach((date) => {
      const dayPresent = attendanceData.filter(
        (s) => s.attendance[date] === 'P',
      ).length;
      html += `<td class="text-success">${dayPresent}</td>`;
    });
    html += `<td colspan="4">-</td></tr>`;

    // Daily Absent Row
    html += `<tr class="summary-row">`;
    html += `<td colspan="2" style="text-align: left;">Daily Absent</td>`;
    dates.forEach((date) => {
      const dayAbsent = attendanceData.filter(
        (s) => s.attendance[date] === 'A',
      ).length;
      html += `<td class="text-danger">${dayAbsent}</td>`;
    });
    html += `<td colspan="4">-</td></tr>`;

    // Daily Percentage Row
    html += `<tr class="summary-row">`;
    html += `<td colspan="2" style="text-align: left;">Daily %</td>`;
    dates.forEach((date) => {
      const dayPresent = attendanceData.filter(
        (s) => s.attendance[date] === 'P',
      ).length;
      const dayTotal = attendanceData.filter(
        (s) => s.attendance[date] === 'P' || s.attendance[date] === 'A',
      ).length;
      const dayPercentage =
        dayTotal > 0 ? ((dayPresent / dayTotal) * 100).toFixed(1) : 0;
      html += `<td>${dayPercentage}%</td>`;
    });
    html += `<td colspan="4">-</td></tr>`;

    html += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleExcelDownload = () => {
    if (attendanceData.length === 0) {
      alert('No attendance data to download');
      return;
    }

    const selectedBatchName = batches.find(
      (b) => b.id === parseInt(selectedBatch),
    );
    const batchName = selectedBatchName
      ? `${selectedBatchName.class_name}_${selectedBatchName.division}`
      : 'Attendance';
    const monthName = new Date(month + '-01').toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });

    const dates = Object.keys(attendanceData[0].attendance);
    const sortedData = getSortedData();

    // Create CSV content
    let csv = `${batchName} - Monthly Attendance - ${monthName}\n`;

    if (sortConfig.key) {
      let columnName = '';
      if (sortConfig.key === 'name') columnName = 'Student Name';
      else if (sortConfig.key === 'present') columnName = 'Present';
      else if (sortConfig.key === 'absent') columnName = 'Absent';
      else if (sortConfig.key === 'total') columnName = 'Total';
      else if (sortConfig.key === 'percentage') columnName = 'Percentage';
      else if (sortConfig.key.startsWith('date-')) {
        const date = sortConfig.key.replace('date-', '');
        columnName = `Date ${new Date(date).getDate()}`;
      }
      const direction =
        sortConfig.direction === 'asc' ? 'Ascending' : 'Descending';
      csv += `Sorted by: ${columnName} (${direction})\n`;
    }
    csv += `\n`;

    // Header row
    csv += `#,Student Name,`;
    dates.forEach((date) => {
      csv += `${new Date(date).getDate()},`;
    });
    csv += `Present,Absent,Total,%\n`;

    // Data rows
    sortedData.forEach((student, index) => {
      const statuses = Object.values(student.attendance);
      const presentCount = statuses.filter((s) => s === 'P').length;
      const absentCount = statuses.filter((s) => s === 'A').length;
      const totalDays = presentCount + absentCount;
      const percentage =
        totalDays > 0 ? ((presentCount / totalDays) * 100).toFixed(1) : 0;

      csv += `${index + 1},"${student.name}",`;
      Object.values(student.attendance).forEach((status) => {
        csv += `${status === 'P' || status === 'A' ? status : ''},`;
      });
      csv += `${presentCount},${absentCount},${totalDays},${percentage}%\n`;
    });

    // Summary rows
    csv += `\n`;
    csv += `Daily Present,`;
    dates.forEach((date) => {
      const dayPresent = sortedData.filter(
        (s) => s.attendance[date] === 'P',
      ).length;
      csv += `${dayPresent},`;
    });
    csv += `\n`;

    csv += `Daily Absent,`;
    dates.forEach((date) => {
      const dayAbsent = sortedData.filter(
        (s) => s.attendance[date] === 'A',
      ).length;
      csv += `${dayAbsent},`;
    });
    csv += `\n`;

    csv += `Daily %,`;
    dates.forEach((date) => {
      const dayPresent = sortedData.filter(
        (s) => s.attendance[date] === 'P',
      ).length;
      const dayTotal = sortedData.filter(
        (s) => s.attendance[date] === 'P' || s.attendance[date] === 'A',
      ).length;
      const dayPercentage =
        dayTotal > 0 ? ((dayPresent / dayTotal) * 100).toFixed(1) : 0;
      csv += `${dayPercentage}%,`;
    });
    csv += `\n`;

    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${batchName}_${month}_Attendance.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Attendance</h4>

        <div className="d-flex gap-2">
          <button
            className="btn btn-success"
            onClick={handleExcelDownload}
            disabled={attendanceData.length === 0}
          >
            <i className="fas fa-file-excel me-2"></i>
            Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handlePrint}
            disabled={attendanceData.length === 0}
          >
            <i className="fas fa-print me-2"></i>
            Print
          </button>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>
      <div className="d-flex gap-3 mb-3">
        <Form.Select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
        >
          {batches?.length > 0 && (
            <>
              {batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.class_name + ' ' + batch.division}
                </option>
              ))}
            </>
          )}
        </Form.Select>
        <Form.Control
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>

      {attendanceData.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th
                  className="text-center"
                  style={{
                    minWidth: '40px',
                    position: 'sticky',
                    left: 0,
                    background: '#fff',
                    zIndex: 2,
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    minWidth: '200px',
                    position: 'sticky',
                    left: '40px',
                    background: '#fff',
                    zIndex: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('name')}
                >
                  Student {getSortIcon('name')}
                </th>

                {Object.keys(attendanceData[0].attendance).map((date) => (
                  <th
                    key={date}
                    className="text-center"
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={() => handleSort(`date-${date}`)}
                  >
                    {new Date(date).getDate()} {getSortIcon(`date-${date}`)}
                  </th>
                ))}

                <th
                  className="text-center bg-light"
                  style={{
                    minWidth: '95px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('present')}
                >
                  Present {getSortIcon('present')}
                </th>
                <th
                  className="text-center bg-light"
                  style={{
                    minWidth: '90px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('absent')}
                >
                  Absent {getSortIcon('absent')}
                </th>
                <th
                  className="text-center bg-light"
                  style={{
                    minWidth: '85px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('total')}
                >
                  Total {getSortIcon('total')}
                </th>
                <th
                  className="text-center bg-light"
                  style={{
                    minWidth: '75px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleSort('percentage')}
                >
                  % {getSortIcon('percentage')}
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedData().map((student, index) => {
                const statuses = Object.values(student.attendance);
                const presentCount = statuses.filter((s) => s === 'P').length;
                const absentCount = statuses.filter((s) => s === 'A').length;
                const totalDays = presentCount + absentCount;
                const percentage =
                  totalDays > 0
                    ? ((presentCount / totalDays) * 100).toFixed(1)
                    : 0;

                return (
                  <tr key={student.student_id}>
                    <td
                      className="text-center"
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: '#fff',
                        zIndex: 1,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      style={{
                        position: 'sticky',
                        left: '40px',
                        background: '#fff',
                        zIndex: 1,
                      }}
                    >
                      {student.name}
                    </td>
                    {Object.values(student.attendance).map((status, index) => (
                      <td
                        key={index}
                        className={`text-center ${
                          status === 'P'
                            ? 'table-success'
                            : status === 'A'
                            ? 'table-danger'
                            : ''
                        }`}
                      >
                        {status === 'P' || status === 'A' ? status : ''}
                      </td>
                    ))}
                    <td className="text-center fw-bold text-success bg-light">
                      {presentCount}
                    </td>
                    <td className="text-center fw-bold text-danger bg-light">
                      {absentCount}
                    </td>
                    <td className="text-center fw-bold bg-light">
                      {totalDays}
                    </td>
                    <td className="text-center fw-bold bg-light">
                      {percentage}%
                    </td>
                  </tr>
                );
              })}

              {/* Summary Row - Present */}
              <tr className="table-secondary fw-bold">
                <td
                  colSpan="2"
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: '#e2e3e5',
                    zIndex: 1,
                  }}
                >
                  Daily Present
                </td>
                {Object.keys(attendanceData[0].attendance).map((date) => {
                  const dayPresent = attendanceData.filter(
                    (s) => s.attendance[date] === 'P',
                  ).length;
                  return (
                    <td key={date} className="text-center text-success">
                      {dayPresent}
                    </td>
                  );
                })}
                <td className="text-center" colSpan="4">
                  -
                </td>
              </tr>

              {/* Summary Row - Absent */}
              <tr className="table-secondary fw-bold">
                <td
                  colSpan="2"
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: '#e2e3e5',
                    zIndex: 1,
                  }}
                >
                  Daily Absent
                </td>
                {Object.keys(attendanceData[0].attendance).map((date) => {
                  const dayAbsent = attendanceData.filter(
                    (s) => s.attendance[date] === 'A',
                  ).length;
                  return (
                    <td key={date} className="text-center text-danger">
                      {dayAbsent}
                    </td>
                  );
                })}
                <td className="text-center" colSpan="4">
                  -
                </td>
              </tr>

              {/* Percentage Row */}
              <tr className="table-secondary fw-bold">
                <td
                  colSpan="2"
                  style={{
                    position: 'sticky',
                    left: 0,
                    background: '#e2e3e5',
                    zIndex: 1,
                  }}
                >
                  Daily %
                </td>
                {Object.keys(attendanceData[0].attendance).map((date) => {
                  const dayPresent = attendanceData.filter(
                    (s) => s.attendance[date] === 'P',
                  ).length;
                  const dayTotal = attendanceData.filter(
                    (s) =>
                      s.attendance[date] === 'P' || s.attendance[date] === 'A',
                  ).length;
                  const dayPercentage =
                    dayTotal > 0
                      ? ((dayPresent / dayTotal) * 100).toFixed(1)
                      : 0;
                  return (
                    <td key={date} className="text-center">
                      {dayPercentage}%
                    </td>
                  );
                })}
                <td className="text-center" colSpan="4">
                  -
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}
    </Layout>
  );
};

export default MonthlyAttendancePage;
